import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import PaymentIconSprite from '../ui/PaymentIcon';

/**
 * PlacePaymentCascadeConfirmDialog
 *
 * Dialog de confirmação que mostra o impacto em cascata da remoção
 * de formas de pagamento de um local.
 *
 * Props:
 * - open: boolean
 * - cascades: Array de PlacePaymentCascadePreviewDto
 * - onCancel: função para cancelar
 * - onConfirm: função para confirmar a cascata
 */
export default function PlacePaymentCascadeConfirmDialog({
  open,
  cascades = [],
  onCancel,
  onConfirm,
}) {
  const handleCancel = () => {
    onCancel?.();
  };

  const handleConfirm = () => {
    onConfirm?.();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon sx={{ color: 'warning.main', fontSize: 28 }} />
          <Typography variant="h6">
            Confirmar remoção — impacto em cascata
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Estas customizações serão excluídas e não podem ser recuperadas.
        </Alert>

        {cascades && cascades.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {cascades.map((cascade) => (
              <Paper key={cascade.paymentId} sx={{ p: 2, bgcolor: 'background.default' }}>
                {/* Payment name header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <PaymentIcon displayOrder={1} size={24} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {cascade.paymentName || 'Forma de pagamento'}
                  </Typography>
                </Box>

                {/* Services section */}
                {cascade.services && cascade.services.length > 0 ? (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}
                    >
                      Serviços afetados ({cascade.services.length})
                    </Typography>
                    <Box sx={{ mt: 0.75, ml: 1 }}>
                      {cascade.services.map((service, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 0.5,
                            px: 0.75,
                            borderRadius: 0.5,
                            bgcolor: 'rgba(0, 0, 0, 0.02)',
                            mb: 0.5,
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', minWidth: 0, flex: 1 }}>
                            {service.serviceName || 'Serviço sem nome'}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.85rem', color: 'text.secondary', ml: 1, flexShrink: 0 }}
                          >
                            {service.defaultPrice !== null && service.defaultPrice !== undefined
                              ? `R$ ${service.defaultPrice.toFixed(2)}`
                              : '—'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}

                {/* Professionals section */}
                {cascade.professionals && cascade.professionals.length > 0 ? (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{ textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}
                    >
                      Profissionais afetados ({cascade.professionals.length})
                    </Typography>
                    <Box sx={{ mt: 0.75, ml: 1 }}>
                      {cascade.professionals.map((professional, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 0.5,
                            px: 0.75,
                            borderRadius: 0.5,
                            bgcolor: 'rgba(0, 0, 0, 0.02)',
                            mb: 0.5,
                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', minWidth: 0, flex: 1 }}>
                            <Box component="span" fontWeight={500}>
                              {professional.professionalName || 'Profissional sem nome'}
                            </Box>
                            <Box component="span" color="text.secondary" sx={{ ml: 0.5 }}>
                              / {professional.serviceName || 'Serviço sem nome'}
                            </Box>
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.85rem', color: 'text.secondary', ml: 1, flexShrink: 0 }}
                          >
                            {professional.customPrice !== null && professional.customPrice !== undefined
                              ? `R$ ${professional.customPrice.toFixed(2)}`
                              : '—'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}

                {/* Empty state */}
                {(!cascade.services || cascade.services.length === 0) &&
                  (!cascade.professionals || cascade.professionals.length === 0) && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Sem customizações — será apenas descredenciada.
                    </Typography>
                  )}
              </Paper>
            ))}
          </Box>
        ) : (
          <Alert severity="info">
            Nenhuma forma de pagamento para remover.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ gap: 1 }}>
        <Button onClick={handleCancel} variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          autoFocus
        >
          Confirmar remoção em cascata
        </Button>
      </DialogActions>
    </Dialog>
  );
}

PlacePaymentCascadeConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  cascades: PropTypes.arrayOf(
    PropTypes.shape({
      paymentId: PropTypes.string,
      paymentName: PropTypes.string,
      services: PropTypes.arrayOf(
        PropTypes.shape({
          serviceName: PropTypes.string,
          defaultPrice: PropTypes.number,
        })
      ),
      professionals: PropTypes.arrayOf(
        PropTypes.shape({
          professionalName: PropTypes.string,
          serviceName: PropTypes.string,
          customPrice: PropTypes.number,
        })
      ),
    })
  ),
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
};
